import { parseErrorResponse } from '@bridge/utils/http';
import { useServerState } from 'providers/ServerStateProvider';
import { type ResponseErrorType } from 'types/infrastructure/http';

import { notifyPendingApproval, PendingApprovalError } from 'client-apis/helpers/pending-approval';
import { notifyUnauthorized } from 'client-apis/helpers/unauthorized';
import env from 'lib/env';

import type { ServerActionRequest, ServerActionResponse } from 'app/api/server-action/[action]/route';
import type { ServerActionPath } from 'server/actions/registry';
import type { BaseMutationState } from 'server/helpers/mutation-wrapper';

/**
 * Error thrown by {@link callServerAction} when a server action fails. Carries the
 * API's `code`/`status`/`source`/`requirements` while being a real `Error` instance.
 *
 * Throwing a plain object (the previous behavior) surfaced as an *unhandled promise
 * rejection* under React Query's streamed hydration — bypassing React error boundaries
 * and triggering Next.js's bare-bones "Application error" full-page crash. A real Error
 * is delivered to the nearest error boundary instead, and groups correctly in Sentry.
 * See BRIDGE-DASHBOARD-10X.
 */
export class ServerActionError extends Error implements ResponseErrorType {
  readonly code: string;
  readonly status?: number;
  readonly source?: ResponseErrorType['source'];
  readonly requirements?: ResponseErrorType['requirements'];

  constructor(error: ResponseErrorType) {
    super(error.message);
    this.name = 'ServerActionError';
    this.code = error.code;
    this.status = error.status;
    this.source = error.source;
    this.requirements = error.requirements;
  }
}

/** True when a thrown value represents an HTTP 401 (expired/invalid session). */
export function isUnauthorizedError(error: unknown): boolean {
  if (error instanceof ServerActionError) return error.status === 401;
  const status = (error as { status?: unknown } | null)?.status;
  return status === 401;
}

/** True only for a 401 whose session is dead; other 401s are authorization refusals on a valid session. */
export function isExpiredSessionError(error: unknown): boolean {
  if (!isUnauthorizedError(error)) return false;
  if (typeof error !== 'object' || error === null) return false;
  const code = 'code' in error ? error.code : undefined;
  const message = 'message' in error && typeof error.message === 'string' ? error.message : '';
  return (
    code === 'session_not_found' || message.includes('Bad Session Token') || message.startsWith('Invalid credentials')
  );
}

// * Server Action API helpers

/**
 * Gets the full URL for the API endpoint, handling both client and server environments.
 * During SSR, we need a full URL, not a relative path.
 */
function getApiUrl(path: string): string {
  // Check if we're on the server (SSR)
  if (typeof window === 'undefined') {
    // Use the environment variable for the domain or fallback to localhost
    const domain = env.DOMAIN || 'http://localhost:3000';
    return `${domain}${path}`;
  }
  // On the client, we can use relative URLs
  return path;
}

/**
 * Executes a server action through the API proxy endpoint and returns the same
 * {@link BaseMutationState} envelope that `withMutationHandling` produces server-side.
 * Mirroring that wrapper, every outcome is a value — success, error, or a 202
 * `pendingApproval` — and nothing is thrown (network/non-JSON failures are caught and
 * returned as `{ success: false, error }`). {@link callServerAction} adapts this envelope
 * to its bare-data/throw contract.
 */
async function requestServerAction<T = unknown>(
  action: ServerActionPath,
  params: unknown[] = []
): Promise<BaseMutationState<T>> {
  try {
    // Check if the current page URL has ?showResponse=true and pass it along
    let apiUrl = getApiUrl(`/api/server-action/${action}`);
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href);
      const showResponse = currentUrl.searchParams.get('showResponse');
      if (showResponse === 'true') {
        apiUrl += '?showResponse=true';
      }
    }

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
        ...(typeof window === 'undefined'
          ? {
              // SSR suspense queries POST to this same-origin route and must forward the session cookie because client-component SSR cannot access next/headers.
              Cookie: useServerState().headers.cookie ?? '',
            }
          : {}),
      },
      body: JSON.stringify({
        params,
      } satisfies ServerActionRequest),
    });

    // Handle 413 responses. The body shape varies by source:
    //   1. ALB / CDN 413 — HTML body
    //   2. CDN/middleware 413 — flat ResponseErrorType JSON ({ code, message })
    //   3. Server-action route propagating an upstream 413 — envelope JSON ({ success:false, error })
    // We parse once and unwrap the envelope form if needed.
    if (response.status === 413) {
      const parsed = await parseErrorResponse(response);
      if (
        parsed &&
        typeof parsed === 'object' &&
        'success' in parsed &&
        (parsed as { success?: boolean }).success === false
      ) {
        const envelope = parsed as unknown as ServerActionResponse<T>;
        const error: ResponseErrorType =
          envelope.error ??
          ({ code: 'PAYLOAD_TOO_LARGE', message: 'Payload too large' } as unknown as ResponseErrorType);
        (error as { status?: number }).status = envelope.status ?? response.status;
        return { success: false, error, status: error.status };
      }
      (parsed as { status?: number }).status = parsed.status ?? response.status;
      return { success: false, error: parsed, status: parsed.status };
    }

    const result = (await response.json()) as ServerActionResponse<T>;

    if (!result.success) {
      // Prefer the ResponseErrorType returned by the API route.
      // Fallback to constructing one with the HTTP status if needed.
      const status = result.status ?? response.status ?? 500;
      const error: ResponseErrorType =
        result.error ||
        ({
          code: 'ACTION_FAILED',
          message: 'Server action failed',
        } as unknown as ResponseErrorType);
      // Ensure numeric status is present
      (error as unknown as { status?: number }).status = status;
      if (isExpiredSessionError(error)) {
        notifyUnauthorized();
      }
      return { success: false, error, status };
    }

    if (result.pendingApproval) {
      return { success: true, status: result.status ?? 202, pendingApproval: result.pendingApproval };
    }

    return { success: true, data: result.data as T, status: result.status };
  } catch (error) {
    // Network/non-JSON failure: build a real ResponseErrorType so the rethrown ServerActionError
    // has a defined code/status instead of the raw caught value's undefined fields.
    return {
      success: false,
      error: {
        code: 'UNKNOWN_ERROR',
        message: error instanceof Error ? error.message : 'Server action request failed',
        status: 500,
      },
      status: 500,
    };
  }
}

/**
 * Calls a server action through the API proxy endpoint, returning the action's data.
 * @param action - The dot-notation path to the action (e.g., 'users.getUsers')
 * @param params - Array of parameters to pass to the action
 * @returns Promise with the action result, or throws on failure.
 *
 * Approval handling is automatic: if the action is gated behind a sensitive-action approval
 * rule (HTTP 202), the registered handler surfaces the "awaiting approval" modal and this
 * throws a {@link PendingApprovalError} so the success path never runs. Callers only catch it
 * to add behavior (e.g. closing a sheet); otherwise it can propagate like any other failure.
 */
export async function callServerAction<T = unknown>(action: ServerActionPath, params: unknown[] = []): Promise<T> {
  const result = await requestServerAction<T>(action, params);
  if (!result.success) {
    throw new ServerActionError(result.error);
  }
  if (result.pendingApproval) {
    notifyPendingApproval(result.pendingApproval.sensitiveActionId);
    throw new PendingApprovalError(result.pendingApproval.sensitiveActionId);
  }
  return result.data as T;
}
